import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  MANAGEMENT_QUERY_KEY_GET_PRODUCT,
  MANAGEMENT_QUERY_KEY_GET_PRODUCTS,
  useManagementCreateProduct,
  useManagementDeleteProduct,
  useManagementGetProduct,
  useManagementGetProductTypes,
  useManagementUpdateProduct
} from '@apps-common/apis/management/product'
import { ManagementProductImage } from '@apps-common/components/common/ManagementProductImage'
import { ButtonRoundedSm, ButtonRoundSm } from '@apps-common/components/elements/button'
import { CheckboxInputSM, ImageSelect, InputSM, SelectInputSM, TextAreaSM } from '@apps-common/components/elements/form'
import { VertSpacerLg, VertSpacerSm } from '@apps-common/components/layout'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { queryClient } from '@apps-common/lib'
import { IProductModel } from '@apps-common/types/management/product'

export const ProductModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: any
}) => {
  const { isLoading: ptIsLoading, isError: ptIsError, data: productTypes } = useManagementGetProductTypes({})
  const [file, setFile] = useState<File | null>()

  const {
    isLoading,
    isError,
    data: product
  } = useManagementGetProduct({
    productId: componentProps?.productId || '',
    config: {
      enabled: !!componentProps?.productId
    }
  })

  console.log(isLoading, isError)

  const {
    reset,
    register,
    getValues,
    handleSubmit,
    formState: { errors }
  } = useForm<IProductModel>({
    defaultValues: {
      imageUrl: ''
    }
  })

  const onChanged = () => {
    queryClient.invalidateQueries({ queryKey: MANAGEMENT_QUERY_KEY_GET_PRODUCT() })
    queryClient.invalidateQueries({ queryKey: MANAGEMENT_QUERY_KEY_GET_PRODUCTS() })
    onDismiss(null, ModalDismiss.Confirm)
  }

  const { isPending: createIsLoading, mutate: createProduct } = useManagementCreateProduct(true, () => onChanged())
  const { isPending: updateIsLoading, mutate: updateProduct } = useManagementUpdateProduct(true, () => onChanged())
  const { isPending: deleteIsLoading, mutate: deleteProduct } = useManagementDeleteProduct(true, () => onChanged())

  useEffect(() => {
    if (product && productTypes) {
      reset(product)
    }
  }, [product, productTypes])

  const onSubmit = async (ptData: any) => {
    try {
      let res

      if (ptData.productId) {
        updateProduct({ model: getValues() as any, image: file })
      } else {
        createProduct({ model: getValues() as any, image: file })
      }
      onDismiss(JSON.stringify(res), 'confirm')
    } catch (e: any) {
      // ignore let mutation handle error
    }
  }

  const loading = isLoading || isError || ptIsLoading || ptIsError
  const updating = createIsLoading || updateIsLoading || deleteIsLoading

  const productImage = (
    <ImageSelect
      onChangeCallback={(imageFile: any) => {
        setFile(imageFile)
      }}
      onClearCallback={(imageFile: any) => {}}
      cropDimensions={{
        width: 200,
        height: 200
      }}
      selectedImageClasses='w-[60px] h-[60px] rounded-[30px] border border-border'
      clearChildren={
        <div className='absolute top-0 right-0 bg-surface rounded-full p-[1px]'>
          <commonIcons.editCircle className='text-text-muted' />
        </div>
      }
      disabled={loading || updating}
    >
      <div className='absolute top-0 right-0 bg-surface rounded-full p-[1px]'>
        <commonIcons.editCircle className='text-text' />
      </div>
      <ManagementProductImage loading={loading} siteName={product?.productName} url={product?.imageUrl} file={file} />
    </ImageSelect>
  )

  return (
    <ModalContainer innerClasses='!min-h-[640px]'>
      <ModalHeaderContainer>
        <div className='flex items-center justify-between'>
          <ModalHeader subtitle={componentProps?.productId ? 'Updating' : 'Creating'} title='Product' subtitleClasses='capitalize' />
          <div className='flex items-center gap-[10px]'>
            {componentProps?.productId && (
              <ModalButton
                component={ConfirmModal}
                classes=''
                disabled={loading || updating}
                componentProps={{
                  text: 'Delete',
                  description: 'Confirm you would like to delete this company. This cannot be undone.',
                  action: ConfirmModalAction.Error
                }}
                confirmCallback={() => deleteProduct({ productId: componentProps?.productId })}
              >
                <ButtonRoundSm type='div' className='action-error'>
                  <commonIcons.delete />
                </ButtonRoundSm>
              </ModalButton>
            )}
            <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='relative !pt-[110px] !pb-[30px]'>
        {(!isLoading && isError) ||
          (!ptIsLoading && ptIsError && (
            <p className='mb-[10px] text-red-500 text-sm'>Product failed to load. Please close window and try again.</p>
          ))}
        <form noValidate id='productModalForm' onSubmit={handleSubmit(onSubmit)}>
          <div className='flex'>{productImage}</div>
          <VertSpacerLg />
          <div>
            <SelectInputSM
              type='text'
              placeholder='Type of the product'
              label='Product type'
              id='productTypeId'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('productTypeId', {
                required: FORM_DEFAULT_REQUIRED
              })}
              options={productTypes?.map((pt) => ({ label: pt.productType, value: pt.productTypeId })) || []}
              errors={errors.productName}
            />
            <InputSM
              type='text'
              placeholder='Name of the product'
              label='Product name'
              id='productName'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('productName', {
                required: FORM_DEFAULT_REQUIRED
              })}
              errors={errors.productName}
            />
            <TextAreaSM
              type='text'
              placeholder='Product description'
              label='Description'
              id='description'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('description')}
              errors={errors.description}
            />
            <InputSM
              type='number'
              placeholder='Price of the product'
              label='Product price'
              id='productPrice'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('price', {
                required: FORM_DEFAULT_REQUIRED
              })}
              errors={errors.price}
            />
            <VertSpacerSm />
            <CheckboxInputSM
              label='Is the default product'
              id='defaultItem'
              loading={loading}
              disabled={updating}
              registration={register('defaultItem')}
              errors={errors.defaultItem}
            />
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-2'>
          <ButtonRoundedSm
            form='productModalForm'
            type='submit'
            loading={loading || updating}
            text={product ? 'Update' : 'Create'}
            className='action-success sm:grow-0'
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
